import { useEffect, useMemo, useRef, useState } from 'react'
import {
  CardTemplateProps,
  ETypeOfNotification,
  InputTextCustom,
  Modal,
  RColumn,
  RemoteTable,
  RemoteTableRef,
  setNotification,
  stringFilterTemplate,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { useDispatch } from 'react-redux'
import {
  DoseFrequencyStartingHourListItemDTO,
  DoseFrequencyStartingHourListParams,
} from '../../dto/dosefrequencies/DoseFrequenciesDTO'
import { useDoseFrequenciesService } from '../../servicesHooks/DoseFrequencies/useDoseFrequenciesService'
import useResizeListener from '../../hooks/useResizeListener'
import LocationInlineEditActions from '../Location/components/LocationInlineEditActions'
import LocationActionMenu, { LocationActionItem } from '../Location/tabs/components/LocationActionMenu'
import './StartingHourForDoseFrequencyView.scss'

type UnknownRecord = Record<string, unknown>

const MOBILE_SEARCH_BATCH_SIZE = 200
const MOBILE_FILTER_DEBOUNCE_MS = 300

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function normalizeText(value: string | number | null | undefined) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function getFilterText(input: unknown): string {
  if (typeof input === 'string') return input
  if (!isRecord(input)) return ''
  const target = isRecord(input.target) ? input.target : null
  return typeof target?.value === 'string' ? target.value : ''
}

function formatStartingHour(hour: number | null) {
  return hour != null ? `${hour} h` : '-'
}

function formatDoseFrequencyLabel(label: string, doseFrequencyId: number) {
  const trimmed = label.trim()
  const match = trimmed.match(/(\d+)/)
  const hourValue = match?.[1] ?? String(doseFrequencyId)

  if (hourValue !== '') {
    return `Cada ${hourValue} Horas`
  }

  return trimmed === '' ? '-' : trimmed
}

function matchesMobileFilter(item: DoseFrequencyStartingHourListItemDTO, normalizedFilterText: string) {
  if (normalizedFilterText === '') return true

  return [
    formatDoseFrequencyLabel(item.doseFrequencyLabel, item.doseFrequencyId),
    item.startingHour != null ? String(item.startingHour) : '',
  ].some((value) => normalizeText(value).includes(normalizedFilterText))
}

function extractErrorMessage(error: unknown): string {
  if (!isRecord(error) || !isRecord(error.response)) {
    return 'No se pudo guardar la hora de comienzo.'
  }

  const data = error.response.data
  if (typeof data === 'string' && data.trim() !== '') {
    return data
  }

  return 'No se pudo guardar la hora de comienzo.'
}

interface DoseFrequencyCardActionsProps {
  onEdit: (item: DoseFrequencyStartingHourListItemDTO) => void
}

type DoseFrequencyListCardProps = CardTemplateProps & DoseFrequencyCardActionsProps & {
  entity: DoseFrequencyStartingHourListItemDTO
}

function DoseFrequencyListCard({
  entity,
  onEdit,
}: Readonly<DoseFrequencyListCardProps>) {
  const cardMenuItems: LocationActionItem[] = useMemo(() => [
    {
      label: 'Editar',
      icon: 'pi pi-file-edit',
      action: () => onEdit(entity),
    },
  ], [entity, onEdit])

  return (
    <article className='dose-frequency-mobile-card'>
      <div className='dose-frequency-mobile-card-header'>
        <h3 className='dose-frequency-mobile-card-title'>
          {formatDoseFrequencyLabel(entity.doseFrequencyLabel, entity.doseFrequencyId)}
        </h3>
        <LocationActionMenu
          items={cardMenuItems}
          buttonClassName='p-button-text dose-frequency-mobile-card-menu-btn'
          ariaLabel='Opciones de la frecuencia'
          panelClassName='dose-frequency-action-menu-panel'
          contentClassName='w-64'
        />
      </div>

      <div className='dose-frequency-mobile-card-body'>
        <div className='dose-frequency-mobile-card-row'>
          <span className='dose-frequency-mobile-card-key'>Hora de comienzo</span>
          <span className='dose-frequency-mobile-card-value'>{formatStartingHour(entity.startingHour)}</span>
        </div>
      </div>
    </article>
  )
}

export default function StartingHourForDoseFrequencyView() {
  const { getDoseFrequencyStartingHours, updateDoseFrequencyStartingHour } = useDoseFrequenciesService()
  const dispatch = useDispatch()
  const tableRef = useRef<RemoteTableRef>(null)
  const isMobile = useResizeListener(768)
  const isMobileRef = useRef(isMobile)
  const [mobileFilterDraft, setMobileFilterDraft] = useState('')
  const [mobileFilterText, setMobileFilterText] = useState('')
  const [editingRowId, setEditingRowId] = useState<number | null>(null)
  const editingRowIdRef = useRef<number | null>(null)
  const [editingValue, setEditingValue] = useState('')
  const editingValueRef = useRef('')
  const [savingRowId, setSavingRowId] = useState<number | null>(null)
  const [validationError, setValidationError] = useState('')
  const validationErrorRef = useRef('')
  const [mobileModalVisible, setMobileModalVisible] = useState(false)

  isMobileRef.current = isMobile
  editingRowIdRef.current = editingRowId
  editingValueRef.current = editingValue
  validationErrorRef.current = validationError

  function clearTableFiltersStorage() {
    const tableStateKeys = ['DoseFrequenciesTable', 'mobileFilter_DoseFrequenciesTable']
    tableStateKeys.forEach((key) => {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    })
  }

  useEffect(() => {
    clearTableFiltersStorage()
    return () => {
      clearTableFiltersStorage()
    }
  }, [])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setMobileFilterText(mobileFilterDraft)
    }, MOBILE_FILTER_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [mobileFilterDraft])

  useEffect(() => {
    tableRef.current?.refreshTable?.()
  }, [mobileFilterText])

  function resetEditingState() {
    setEditingRowId(null)
    setEditingValue('')
    setSavingRowId(null)
    setValidationError('')
    setMobileModalVisible(false)
  }

  function openEdit(item: DoseFrequencyStartingHourListItemDTO) {
    if (savingRowId != null) return

    setEditingRowId(item.doseFrequencyId)
    setEditingValue(item.startingHour != null ? String(item.startingHour) : '')
    setValidationError('')
    setMobileModalVisible(isMobile)
  }

  function cancelEdit() {
    resetEditingState()
  }

  function validateEditingValue(value: string) {
    const trimmed = value.trim()
    if (trimmed === '') {
      return null
    }
    if (!/^\d+$/.test(trimmed)) {
      return 'La hora de comienzo no me permite signos'
    }

    const parsed = Number(trimmed)
    if (!Number.isInteger(parsed) || parsed < 1 || parsed > 24) {
      return 'La hora de comienzo debe estar entre 1 y 24.'
    }

    return null
  }

  async function confirmEdit() {
    if (editingRowId == null) return

    const validationMessage = validateEditingValue(editingValue)
    if (validationMessage != null) {
      setValidationError(validationMessage)
      return
    }

    setSavingRowId(editingRowId)
    setValidationError('')

    try {
      await updateDoseFrequencyStartingHour(editingRowId, {
        startingHour: editingValue.trim() === '' ? null : Number(editingValue.trim()),
      })

      dispatch(setNotification({
        title: 'Datos guardados con éxito',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))

      resetEditingState()
      tableRef.current?.refreshTable?.()
    } catch (error: unknown) {
      setValidationError(extractErrorMessage(error))
      setSavingRowId(null)

      dispatch(setNotification({
        title: 'No se pudo guardar',
        desc: extractErrorMessage(error),
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
    }
  }

  async function loadDoseFrequencies(
    offSet: number,
    pageSize: number,
    params: DoseFrequencyStartingHourListParams = {},
  ) {
    const normalizedMobileFilterText = normalizeText(mobileFilterText)

    if (normalizedMobileFilterText === '') {
      return getDoseFrequencyStartingHours(offSet, pageSize, params)
    }

    const batch = await getDoseFrequencyStartingHours(0, Math.max(pageSize, MOBILE_SEARCH_BATCH_SIZE), {
      ...params,
      cardFilterDTO: null,
    })

    return batch
      .filter((item) => matchesMobileFilter(item, normalizedMobileFilterText))
      .slice(offSet, offSet + pageSize)
  }

  const cardActionsProps = useMemo<DoseFrequencyCardActionsProps>(() => ({
    onEdit: openEdit,
  }), [isMobile, savingRowId])

  const rowActions = useMemo(() => (rowData: DoseFrequencyStartingHourListItemDTO) => {
    const isEditingRow = !isMobile && editingRowId === rowData.doseFrequencyId
    const isSavingRow = savingRowId === rowData.doseFrequencyId

    if (isEditingRow) {
      return (
        <LocationInlineEditActions
          visible
          onConfirm={() => { void confirmEdit() }}
          onCancel={cancelEdit}
          confirmTooltip='Confirmar'
          cancelTooltip='Cancelar'
        />
      )
    }

    return (
      <Button
        type='button'
        className='p-button-text dose-frequency-edit-icon-btn'
        icon='pi pi-file-edit'
        aria-label='Editar hora de comienzo'
        tooltip='Editar'
        tooltipOptions={{ position: 'top' }}
        disabled={editingRowId != null || isSavingRow}
        onClick={() => openEdit(rowData)}
      />
    )
  }, [confirmEdit, editingRowId, isMobile, savingRowId])

  const tableParams = useMemo(() => ({}), [])

  const tableRenderKey = useMemo(
    () => `dose-frequencies-table-${mobileFilterText}`,
    [mobileFilterText],
  )

  function handleMobileFilterChange(input: unknown) {
    setMobileFilterDraft(getFilterText(input))
  }

  function handleTableClearFiltersClick(event: React.MouseEvent<HTMLDivElement>) {
    const target = event.target
    if (!(target instanceof HTMLElement)) return

    const clearButton = target.closest('button[aria-label="Limpiar filtros"]')
    if (!clearButton) return

    clearTableFiltersStorage()
    window.setTimeout(() => {
      setMobileFilterDraft('')
      setMobileFilterText('')
    }, 0)
  }

  return (
    <div className='viewContainer dose-frequency-view' onClick={handleTableClearFiltersClick}>
      <div className='dose-frequency-header'>
        <h1 className='dose-frequency-title'>Frecuencia de dosis</h1>
      </div>
      <div className='dose-frequency-divider' />

      <RemoteTable
        key={tableRenderKey}
        idTable='DoseFrequenciesTable'
        dataKey='doseFrequencyId'
        className='dose-frequency-table'
        getItems={loadDoseFrequencies}
        resizableColumns
        dynamicBehaviour
        CardTemplate={DoseFrequencyListCard}
        cardKeyName='doseFrequencyId'
        cardAdditionalProps={cardActionsProps}
        handleMobileFilter={handleMobileFilterChange}
        params={tableParams}
        ref={tableRef}
        filterRow
        rowActions={rowActions}
      >
        <RColumn
          field='doseFrequencyLabel'
          className='column'
          header='Frecuencia'
          filter
          filterElement={stringFilterTemplate}
          body={(item: DoseFrequencyStartingHourListItemDTO) => formatDoseFrequencyLabel(
            item.doseFrequencyLabel,
            item.doseFrequencyId,
          )}
        />
        <RColumn
          field='startingHour'
          className='column dose-frequency-table-hour-column'
          header='Hora de comienzo de aplicación'
          body={(item: DoseFrequencyStartingHourListItemDTO) => {
            const isEditingRow = !isMobileRef.current && editingRowIdRef.current === item.doseFrequencyId

            if (!isEditingRow) {
              return formatStartingHour(item.startingHour)
            }

            return (
              <div className='dose-frequency-inline-editor'>
                <InputTextCustom
                  value={editingValueRef.current}
                  onChange={(event: any) => {
                    setEditingValue(String(event?.target?.value ?? '').slice(0, 2))
                    if (validationErrorRef.current !== '') setValidationError('')
                  }}
                  error={validationErrorRef.current !== ''}
                  caption={validationErrorRef.current}
                  className='dose-frequency-inline-input'
                />
                <span >h</span>
              </div>
            )
          }}
        />
      </RemoteTable>

      <Modal
        visible={mobileModalVisible}
        setVisible={setMobileModalVisible}
        header='Editar hora de comienzo'
        footerButtonRightText='Guardar'
        footerButtonLeftText='Cancelar'
        onClickRightBtn={() => { void confirmEdit() }}
        onClickLeftBtn={cancelEdit}
      >
        <div className='dose-frequency-mobile-modal-body'>
          <label className='dose-frequency-mobile-modal-label'>
            Hora de comienzo de aplicación
          </label>
          <InputTextCustom
            value={editingValue}
            onChange={(event: any) => {
              setEditingValue(String(event?.target?.value ?? '').slice(0, 2))
              if (validationError !== '') setValidationError('')
            }}
            error={validationError !== ''}
            caption={validationError}
          />
        </div>
      </Modal>
    </div>
  )
}


